import { useCallback, ChangeEvent } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';

import ValidationMessage from 'approot/shared/validation/validation-message';
import {
  InputDescriptionStyled,
  TextInputStyled,
  TextInputAreaStyled,
} from 'approot/shared/styles/inputs.style';
import { Box } from 'approot/shared/design-system/box/box.style';

type Props = {
  id: string;
  name: string;
  required?: boolean;
  validation?: RegisterOptions;
  placeholder?: string;
  isDisabled?: boolean;
  maxLength: number;
  rows: number;
  defaultValue?: string;
  onChange?: (value: string) => void;
  ['aria-label']?: string;
  onFocus?: () => void;
};

const TextInputLimit = ({
  id,
  name,
  placeholder,
  isDisabled,
  maxLength,
  rows = 2,
  validation,
  required = false,
  defaultValue,
  onChange,
  onFocus,
  ...rest
}: Props) => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      register(name).onChange(e);
      if (value.length > maxLength) {
        const truncatedValue = value.substring(0, maxLength);
        e.preventDefault();
        e.stopPropagation();
        setValue(name, truncatedValue);
        if (onChange) onChange(truncatedValue);
      } else {
        if (onChange) onChange(value);
      }
    },
    [maxLength, name, onChange, register, setValue]
  );

  const fieldValue = watch(name);
  const fieldError = errors[name];

  return (
    <Box $flexDirection="column" $marginBottom={0}>
      {rows && rows > 1 ? (
        <TextInputAreaStyled
          id={id}
          rows={rows}
          data-selector={id}
          {...register(name, validation)}
          placeholder={placeholder}
          aria-label={rest['aria-label'] || placeholder}
          onChange={handleChange}
          defaultValue={defaultValue}
          aria-required={required}
          aria-invalid={!!fieldError}
          aria-describedby={`${name}-error`}
          $hasError={!!fieldError}
          disabled={isDisabled}
          onFocus={onFocus}
        />
      ) : (
        <TextInputStyled
          id={id}
          data-selector={id}
          defaultValue={defaultValue}
          disabled={isDisabled}
          {...register(name, validation)}
          placeholder={placeholder}
          aria-label={rest['aria-label'] || placeholder}
          onChange={handleChange}
          aria-required={required}
          aria-invalid={!!fieldError}
          aria-describedby={`${name}-error`}
          $hasError={!!fieldError}
          onFocus={onFocus}
        />
      )}
      {!fieldError && (
        <InputDescriptionStyled
          data-selector="input-char-value"
          textAlign="right"
        >
          {fieldValue ? fieldValue.length : 0}/{maxLength}
        </InputDescriptionStyled>
      )}
      <ValidationMessage id={`${name}-error`} field={name} errors={errors} />
    </Box>
  );
};

export default TextInputLimit;
